import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { formatDisplayDate } from "../../../../common/utils/date";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStatePlatform } from "../../store/platform-slice";

/**
 * Dashboard "Next breath analysis" card.
 * Visual reference: Figma node 15266:56495.
 *
 * Replaces the previous CircularGraph-based layout with a flat progress
 * bar that's wider, easier to scan, and matches the rest of the redesigned
 * dashboard cards (`AgeClock`, etc).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────┐
 *   │ Next breath analysis                             │
 *   │                                                  │
 *   │ 7 Oct 2024                       50  days left   │
 *   │ ████████████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░    │
 *   │                                                  │
 *   │ Get ready for your next breath analysis for      │
 *   │ RMR & AMR. Keep up with your progress!           │
 *   └──────────────────────────────────────────────────┘
 *
 * Data source:
 *   • `dashboard.nextBreathAnalysis.lastTestDate` — anchor for the bar's left edge.
 *   • `dashboard.nextBreathAnalysis.date`         — anchor for the bar's right edge + label.
 *   The fill shows where "today" sits between those two anchors.
 */
const NextBreathAnalysis: React.FC = () => {
  const theme: Theme = useTheme();

  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer
  );

  const lastTestDate = reportData?.dashboard?.nextBreathAnalysis?.lastTestDate;
  const nextTestDate = reportData?.dashboard?.nextBreathAnalysis?.date;

  // Day-level helpers — keep date math integer-stable across DST boundaries.
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const today = new Date();

  const daysBetween = (
    a: string | null | undefined,
    b: string | null | undefined
  ): number => {
    if (!a || !b) return 0;
    const aMs = new Date(a).getTime();
    const bMs = new Date(b).getTime();
    if (Number.isNaN(aMs) || Number.isNaN(bMs)) return 0;
    return Math.ceil((bMs - aMs) / MS_PER_DAY);
  };

  // Percentage of the cycle that's elapsed (used to drive the bar fill).
  const totalCycleDays = daysBetween(lastTestDate, nextTestDate);
  const elapsedDays = daysBetween(lastTestDate, today.toISOString());
  const rawPercent =
    totalCycleDays > 0 ? (elapsedDays * 100) / totalCycleDays : 0;
  const fillPercent = Math.max(0, Math.min(100, rawPercent));

  // Days remaining until the next test (clamped at 0).
  const rawDaysLeft = daysBetween(today.toISOString(), nextTestDate);
  const daysLeft = Math.max(0, rawDaysLeft);

  const formattedDate = nextTestDate
    ? formatDisplayDate(nextTestDate)
    : "No upcoming test";

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.title(theme)}>Next breath analysis</Box>

      <Box sx={styles.progressBlock}>
        <Box sx={styles.headerRow}>
          <Box sx={styles.date(theme)}>{formattedDate}</Box>
          {nextTestDate && (
            <Box sx={styles.daysLeftWrap}>
              <Box sx={styles.daysLeftValue(theme)}>{daysLeft}</Box>
              <Box sx={styles.daysLeftLabel(theme)}>days left</Box>
            </Box>
          )}
        </Box>

        <Box sx={styles.barTrack(theme)}>
          <Box sx={styles.barFill(theme, fillPercent)} />
        </Box>
      </Box>

      <Box sx={styles.description(theme)}>
        Get ready for your next breath analysis for RMR & AMR. Keep up with
        your progress!
      </Box>
    </Box>
  );
};

export default NextBreathAnalysis;

const styles = {
  container: (theme: Theme) => ({
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  progressBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    width: "100%",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: "100%",
    whiteSpace: "nowrap" as const,
  },
  date: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  daysLeftWrap: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  daysLeftValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  daysLeftLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  barTrack: (theme: Theme) => ({
    width: "100%",
    height: "6px",
    borderRadius: "38px",
    backgroundColor: theme.colorTokens.P[10],
    overflow: "hidden",
  }),
  barFill: (theme: Theme, pct: number) => ({
    height: "100%",
    width: `${pct}%`,
    borderRadius: "38px",
    backgroundColor: theme.colors_ds.PRIMARY_TEXT_MAIN,
    transition: "width 0.3s ease",
  }),
  description: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
